import * as Utils from '../../primitives/utils.js';
const defaultFetch = typeof globalThis !== 'undefined' && typeof globalThis.fetch === 'function'
    ? globalThis.fetch.bind(globalThis)
    : fetch;
/**
 * Implements an HTTP-specific transport for handling Peer mutual authentication messages.
 * This class integrates with fetch to send and receive authenticated messages between peers.
 */
export class SimplifiedFetchTransport {
    onDataCallback;
    fetchClient;
    baseUrl;
    /**
     * Constructs a new instance of SimplifiedFetchTransport.
     * @param baseUrl - The base URL for all HTTP requests made by this transport.
     * @param fetchClient - A fetch implementation to use for HTTP requests (default: global fetch).
     */
    constructor(baseUrl, fetchClient = defaultFetch) {
        if (typeof fetchClient !== 'function') {
            throw new TypeError('SimplifiedFetchTransport requires a fetch implementation. ' +
                'In environments without fetch, provide a polyfill or custom implementation.');
        }
        this.fetchClient = fetchClient;
        this.baseUrl = baseUrl;
    }
    /**
     * Sends a message to an HTTP server using the transport mechanism.
     * Handles both general and authenticated message types. For general messages,
     * the payload is deserialized and sent as an HTTP request. For other message types,
     * the message is sent as a POST request to the `/auth` endpoint.
     *
     * @param message - The AuthMessage to send.
     * @returns A promise that resolves when the message is successfully sent.
     *
     * @throws Will throw an error if no listener has been registered via `onData`.
     */
    async send(message) {
        if (this.onDataCallback == null) {
            throw new Error('Listen before you start speaking. God gave you two ears and one mouth for a reason.');
        }
        if (message.messageType !== 'general')
            return await this.sendAuthMessage(message);
        await this.sendGeneralMessage(message);
    }
    async fetchAuthMessage(url, message) {
        try {
            return await this.fetchClient(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(message)
            });
        }
        catch (error) {
            throw this.createNetworkError(url, error);
        }
    }
    async sendAuthMessage(message) {
        return await new Promise((resolve, reject) => {
            void (async () => {
                try {
                    const url = `${this.baseUrl}/.well-known/auth`;
                    const responsePromise = this.fetchAuthMessage(url, message);
                    if (message.messageType !== 'initialRequest')
                        resolve();
                    const response = await responsePromise;
                    if (!response.ok) {
                        const body = Array.from(new Uint8Array(await response.arrayBuffer()));
                        throw this.createUnauthenticatedResponseError(url, response, body);
                    }
                    await this.onDataCallback((await response.json()));
                    if (message.messageType === 'initialRequest')
                        resolve();
                }
                catch (error) {
                    reject(error);
                }
            })();
        });
    }
    encodeRequestBody(body, contentType) {
        if (contentType.includes('application/json') ||
            contentType.includes('application/x-www-form-urlencoded') ||
            contentType.includes('text/plain')) {
            return Utils.toUTF8(body);
        }
        return new Uint8Array(body);
    }
    prepareGeneralRequest(message) {
        const request = this.deserializeRequestPayload(message.payload);
        if (typeof request.headers !== 'object')
            request.headers = {};
        request.headers['x-bsv-auth-version'] = message.version;
        request.headers['x-bsv-auth-identity-key'] = message.identityKey;
        request.headers['x-bsv-auth-nonce'] = message.nonce;
        request.headers['x-bsv-auth-your-nonce'] = message.yourNonce;
        request.headers['x-bsv-auth-signature'] = Utils.toHex(message.signature);
        request.headers['x-bsv-auth-request-id'] = request.requestId;
        if (request.body != null) {
            const contentType = request.headers['content-type'];
            if (contentType == null) {
                throw new Error('Content-Type header is required for requests with a body.');
            }
            request.body = this.encodeRequestBody(request.body, String(contentType ?? ''));
        }
        return request;
    }
    async fetchGeneralResponse(url, request) {
        try {
            return await this.fetchClient(url, {
                method: request.method,
                headers: request.headers,
                body: request.body
            });
        }
        catch (error) {
            throw this.createNetworkError(url, error);
        }
    }
    validateResponseAuthentication(url, response, body) {
        const missingHeaders = [
            'x-bsv-auth-version',
            'x-bsv-auth-identity-key',
            'x-bsv-auth-signature'
        ].filter(headerName => {
            const value = response.headers.get(headerName);
            return value == null || value.trim().length === 0;
        });
        if (missingHeaders.length > 0) {
            throw this.createUnauthenticatedResponseError(url, response, body, missingHeaders);
        }
    }
    parseRequestedCertificates(url, response) {
        const header = response.headers.get('x-bsv-auth-requested-certificates');
        if (header == null)
            return undefined;
        try {
            return JSON.parse(header);
        }
        catch (error) {
            throw this.createMalformedHeaderError(url, 'x-bsv-auth-requested-certificates', header, error);
        }
    }
    collectSignedResponseHeaders(response) {
        const includedHeaders = [];
        response.headers.forEach((value, key) => {
            const lowerKey = key.toLowerCase();
            const isSignedHeader = lowerKey.startsWith('x-bsv-') || lowerKey === 'authorization';
            if (isSignedHeader && !lowerKey.startsWith('x-bsv-auth')) {
                includedHeaders.push([lowerKey, value]);
            }
        });
        return includedHeaders.sort(([keyA], [keyB]) => keyA.localeCompare(keyB));
    }
    writeGeneralResponsePayload(response, body) {
        const writer = new Utils.Writer();
        const requestId = response.headers.get('x-bsv-auth-request-id');
        if (requestId != null)
            writer.write(Utils.toArray(requestId, 'base64'));
        writer.writeVarIntNum(response.status);
        const includedHeaders = this.collectSignedResponseHeaders(response);
        writer.writeVarIntNum(includedHeaders.length);
        for (const [headerKey, headerValue] of includedHeaders) {
            const keyBytes = Utils.toArray(headerKey, 'utf8');
            const valueBytes = Utils.toArray(headerValue, 'utf8');
            writer.writeVarIntNum(keyBytes.length);
            writer.write(keyBytes);
            writer.writeVarIntNum(valueBytes.length);
            writer.write(valueBytes);
        }
        writer.writeVarIntNum(body.length);
        if (body.length > 0)
            writer.write(body);
        return writer.toArray();
    }
    createGeneralResponseMessage(url, response, body) {
        const message = {
            version: response.headers.get('x-bsv-auth-version'),
            messageType: response.headers.get('x-bsv-auth-message-type') === 'certificateRequest'
                ? 'certificateRequest'
                : 'general',
            identityKey: response.headers.get('x-bsv-auth-identity-key'),
            nonce: response.headers.get('x-bsv-auth-nonce') ?? undefined,
            yourNonce: response.headers.get('x-bsv-auth-your-nonce') ?? undefined,
            requestedCertificates: this.parseRequestedCertificates(url, response),
            payload: this.writeGeneralResponsePayload(response, body),
            signature: Utils.toArray(response.headers.get('x-bsv-auth-signature'), 'hex')
        };
        if (message.version == null) {
            throw this.createUnauthenticatedResponseError(url, response, body);
        }
        return message;
    }
    async sendGeneralMessage(message) {
        const request = this.prepareGeneralRequest(message);
        const url = `${this.baseUrl}${request.urlPostfix}`;
        const response = await this.fetchGeneralResponse(url, request);
        const body = Array.from(new Uint8Array(await response.arrayBuffer()));
        this.validateResponseAuthentication(url, response, body);
        await this.onDataCallback(this.createGeneralResponseMessage(url, response, body));
    }
    /**
     * Registers a callback to handle incoming messages.
     * This must be called before sending any messages to ensure responses can be processed.
     *
     * @param callback - A function to invoke when an incoming AuthMessage is received.
     * @returns A promise that resolves once the callback is set.
     */
    async onData(callback) {
        this.onDataCallback = async (m) => await callback(m);
    }
    createNetworkError(url, originalError) {
        const baseMessage = `Network error while sending authenticated request to ${url}`;
        if (originalError instanceof Error) {
            const error = new Error(`${baseMessage}: ${originalError.message}`);
            error.stack = originalError.stack;
            error.cause = originalError;
            return error;
        }
        return new Error(`${baseMessage}: ${Utils.toSafeString(originalError)}`);
    }
    createUnauthenticatedResponseError(url, response, bodyBytes, missingHeaders = []) {
        const statusText = (response.statusText ?? '').trim();
        const statusDescription = statusText.length > 0 ? `${response.status} ${statusText}` : `${response.status}`;
        const headerMessage = missingHeaders.length > 0
            ? `missing headers: ${missingHeaders.join(', ')}`
            : 'response lacked required BSV auth headers';
        const bodyPreview = this.getBodyPreview(bodyBytes, response.headers.get('content-type'));
        const parts = [
            `Received HTTP ${statusDescription} from ${url} without valid BSV authentication (${headerMessage})`
        ];
        if (bodyPreview != null) {
            parts.push(`body preview: ${bodyPreview}`);
        }
        const error = new Error(parts.join(' - '));
        error.details = {
            url,
            status: response.status,
            statusText: response.statusText,
            missingHeaders,
            bodyPreview
        };
        return error;
    }
    createMalformedHeaderError(url, headerName, headerValue, cause) {
        const errorMessage = `Failed to parse ${headerName} returned by ${url}: ${headerValue}`;
        if (cause instanceof Error) {
            const error = new Error(`${errorMessage}. ${cause.message}`);
            error.stack = cause.stack;
            error.cause = cause;
            return error;
        }
        return new Error(`${errorMessage}. ${Utils.toSafeString(cause)}`);
    }
    getBodyPreview(bodyBytes, contentType) {
        if (bodyBytes.length === 0) {
            return undefined;
        }
        const maxBytesForPreview = 1024;
        const truncated = bodyBytes.length > maxBytesForPreview;
        const slice = truncated ? bodyBytes.slice(0, maxBytesForPreview) : bodyBytes;
        const isText = this.isTextualContent(contentType, slice);
        let preview;
        if (isText) {
            try {
                preview = Utils.toUTF8(slice);
            }
            catch {
                preview = this.formatBinaryPreview(slice, truncated);
            }
        }
        else {
            preview = this.formatBinaryPreview(slice, truncated);
        }
        if (preview.length > 512) {
            preview = `${preview.slice(0, 512)}…`;
        }
        if (truncated) {
            preview = `${preview} (truncated)`;
        }
        return preview;
    }
    isTextualContent(contentType, sample) {
        if (sample.length === 0) {
            return false;
        }
        if (contentType != null) {
            const lowered = contentType.toLowerCase();
            const textualTokens = [
                'application/json',
                'application/problem+json',
                'application/xml',
                'application/xhtml+xml',
                'application/javascript',
                'application/ecmascript',
                'application/x-www-form-urlencoded',
                'text/'
            ];
            if (textualTokens.some(token => lowered.includes(token)) || lowered.includes('charset=')) {
                return true;
            }
        }
        const printableCount = sample.reduce((count, byte) => {
            if (byte === 9 || byte === 10 || byte === 13) {
                return count + 1;
            }
            if (byte >= 32 && byte <= 126) {
                return count + 1;
            }
            return count;
        }, 0);
        return printableCount / sample.length > 0.8;
    }
    formatBinaryPreview(bytes, truncated) {
        const hex = bytes.map(byte => byte.toString(16).padStart(2, '0')).join('');
        return `0x${hex}${truncated ? '…' : ''}`;
    }
    /**
     * Deserializes a request payload from a byte array into an HTTP request-like structure.
     *
     * @param payload - The serialized payload to deserialize.
     * @returns An object representing the deserialized request, including the method,
     *          URL postfix (path and query string), headers, body, and request ID.
     */
    deserializeRequestPayload(payload) {
        // Create a reader
        const requestReader = new Utils.Reader(payload);
        // The first 32 bytes is the requestId
        const requestId = Utils.toBase64(requestReader.read(32));
        // Method
        const methodLength = requestReader.readVarIntNum();
        let method = 'GET';
        if (methodLength > 0) {
            method = Utils.toUTF8(requestReader.read(methodLength));
        }
        // Path
        const pathLength = requestReader.readVarIntNum();
        let path = '';
        if (pathLength > 0) {
            path = Utils.toUTF8(requestReader.read(pathLength));
        }
        // Search
        const searchLength = requestReader.readVarIntNum();
        let search = '';
        if (searchLength > 0) {
            search = Utils.toUTF8(requestReader.read(searchLength));
        }
        // Read headers
        const requestHeaders = {};
        const nHeaders = requestReader.readVarIntNum();
        if (nHeaders > 0) {
            for (let i = 0; i < nHeaders; i++) {
                const nHeaderKeyBytes = requestReader.readVarIntNum();
                const headerKeyBytes = requestReader.read(nHeaderKeyBytes);
                const headerKey = Utils.toUTF8(headerKeyBytes);
                const nHeaderValueBytes = requestReader.readVarIntNum();
                const headerValueBytes = requestReader.read(nHeaderValueBytes);
                const headerValue = Utils.toUTF8(headerValueBytes);
                requestHeaders[headerKey] = headerValue;
            }
        }
        // Read body
        let requestBody;
        const requestBodyBytes = requestReader.readVarIntNum();
        if (requestBodyBytes > 0) {
            requestBody = requestReader.read(requestBodyBytes);
        }
        // Return the deserialized RequestInit
        return {
            urlPostfix: path + search,
            method,
            headers: requestHeaders,
            body: requestBody,
            requestId
        };
    }
}
//# sourceMappingURL=SimplifiedFetchTransport.js.map